import { DEFAULT_FIRM_COLOR_PALETTES } from '../UI/constants/defaultFirmColorPalettes';
import { ScorecardElementSymbol } from '../Scorecard';
import { hexToRgb } from '@mui/material';
import { formatHex, Oklab, oklab, rgb } from 'culori';

export const isDarkTheme = () => {
  if (typeof document === 'undefined') return true;

  const htmlElement = document.getElementsByTagName('html')[0];
  return htmlElement.classList.contains('dark');
};

export const lightModeColors = {
  snow: '#F6F9F9',
  accent: '#19BE81',
  notifications: '#FF2AAC',
  gray: {
    light: '#838C89',
  },
  purple: {
    light: '#DCC3F9',
  },
} as const;

export const darkModeColors = {
  accent: '#4AF196',
  danger: '#FF154F',
  warning: '#FFE26C',
  warningText: '#BFB797',
  white: '#FFFFFF',
  almostBlack: '#101112',
  neutral: '#212326',
  gray: {
    light: '#ACB7B2',
    medium: '#585F66',
    medium20: '#1C1D1F',
    dark: '#17181A',
    dropdown: '#212325',
    floatingMenu: '#1A1B1D',
    background: '#0C0C0E',
    mobileTile: '#585F6629',
  },
} as const;

export const elementsColors: Record<ScorecardElementSymbol, string> = {
  Ir: '#169FFF',
  Er: '#16A104',
  Es: '#00A36C',
  Sr: '#30948F',
  Br: '#C035DD',
  Dr: '#006E42',
  Tr: '#FFBD01',
  Lt: '#FF8C0C',
  Qt: '#8A31F1',
  Rt: '#274ECC',
  Bt: '#FF144F',
  Tt: '#93CE00',
} as const;

export const hiContrastElementsColors: Partial<Record<ScorecardElementSymbol, string>> = {
  Qt: '#B675FF',
  Rt: '#3F66E7',
};

export const elementsTitleTextColors: Record<ScorecardElementSymbol, string> = {
  Ir: darkModeColors.white,
  Er: darkModeColors.white,
  Es: darkModeColors.white,
  Sr: darkModeColors.white,
  Br: darkModeColors.white,
  Dr: darkModeColors.white,
  Tr: darkModeColors.gray.dark,
  Lt: darkModeColors.gray.dark,
  Qt: darkModeColors.white,
  Rt: darkModeColors.white,
  Bt: darkModeColors.gray.dark,
  Tt: darkModeColors.gray.dark,
} as const;

export const elementsTitleTextTailwindClasses: Record<ScorecardElementSymbol, string> = {
  Ir: `text-white`,
  Er: `text-white`,
  Es: `text-white`,
  Sr: `text-white`,
  Br: `text-white`,
  Dr: `text-white`,
  Tr: `text-darkMode-gray-medium mix-blend-multiply`,
  Lt: `text-darkMode-gray-medium mix-blend-multiply`,
  Qt: `text-white`,
  Rt: `text-white`,
  Bt: `text-white`,
  Tt: `text-darkMode-gray-medium mix-blend-multiply`,
} as const;

export function getBrandedText(isLight: boolean) {
  return isLight ? 'text-darkMode-gray-medium mix-blend-multiply' : 'text-darkMode-gray-medium mix-blend-plus-lighter';
}

export function getBlendMode(hex: string) {
  return isLight(hex) ? 'mix-blend-multiply' : 'mix-blend-plus-lighter';
}

export const darkElementsColors: Record<ScorecardElementSymbol, string> = {
  Ir: '#0172C2',
  Er: '',
  Es: '',
  Sr: '',
  Br: '',
  Dr: '',
  Tr: '#a37a00',
  Lt: '',
  Qt: '',
  Rt: '',
  Bt: '',
  Tt: '',
} as const;

export const breakPoints = {
  web: '640px',
} as const;

function getRgbComponentsFromHex(hex: string) {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  } else {
    throw new Error('Invalid Hex Value');
  }
  return [r, g, b];
}

export function quantifyHex(hex: string): number {
  const [r, g, b] = getRgbComponentsFromHex(hex);

  return (r * 299 + g * 587 + b * 114) / 1000;
}

export function isLight(hex: string): boolean {
  return quantifyHex(hex) > 128;
}

export function isDark(hex: string): boolean {
  return quantifyHex(hex) < 45;
}

// Utility function defined above
export function hexToRgba(hex: string, opacity: number) {
  const [r, g, b] = getRgbComponentsFromHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function plusLighterColor(hex: string, greyHex: string = '#585f66'): string {
  const r = Math.min(parseInt(hex.substring(1, 3), 16) + parseInt(greyHex.substring(1, 3), 16), 255);
  const g = Math.min(parseInt(hex.substring(3, 5), 16) + parseInt(greyHex.substring(3, 5), 16), 255);
  const b = Math.min(parseInt(hex.substring(5, 7), 16) + parseInt(greyHex.substring(5, 7), 16), 255);
  return `#${r.toString(16)}${g.toString(16)}${b.toString(16)}`;
}

export interface ColorPalette {
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  1000: string;
  1100: string;
}


/**
 * This algorithm is ok. It shows its inferiority when the input color is really light (darks a little better).
 * The best algorithm out there as of 09/27/2024 is that of https://uicolors.app/create. The palettes always look good,
 * regardless of hue, saturation, or lightness of the input color. Its hue shifting is top notch. Unfortunately, it's
 * just a design tool and API is "coming soon". It might never be open source :(
 * 
 * As a workaround, I have run the tool on a our default firm colors and saved the palettes so that we can use those 
 * instead when applicable.
 * 
 */
export function generateColorScale(inputColorHex: string): string[] {
  const defaultFirmColorPalette = DEFAULT_FIRM_COLOR_PALETTES.find(
    (color) => hexToRgb(color.inputColorHex) === hexToRgb(inputColorHex)
  );

  if (defaultFirmColorPalette) {
    return defaultFirmColorPalette.shades.map((shade) => shade.hex);
  }
  // Parse the input color to Oklab
  const inputColorLab = oklab(inputColorHex) as Oklab;

  if (!inputColorLab) {
    throw new Error('Invalid input color');
  }

  const { a: inputA, b: inputB } = inputColorLab;

  // Define lightness range
  const lightnessRange = [0.96, 0.25]; // Adjusted to produce brighter colors

  // Number of colors in the scale
  const scalePositions = 11;

  // Generate lightness values for the scale
  const lightnessValues: number[] = [];
  for (let i = 0; i < scalePositions; i++) {
    const t = i / (scalePositions - 1);
    const L = lightnessRange[0] - t * (lightnessRange[0] - lightnessRange[1]);
    lightnessValues.push(L);
  }

  // Generate the color scale
  const colorScale: string[] = [];

  for (let i = 0; i < scalePositions; i++) {
    const L = lightnessValues[i];
    let A = inputA;
    let B = inputB;

    // Adjust chroma if necessary to fit within the sRGB gamut
    let labColor: Oklab = { mode: 'oklab', l: L, a: A, b: B };
    let rgbColor = rgb(labColor);

    // If the color is out of gamut, reduce chroma until it fits
    let chroma = Math.sqrt(A * A + B * B);
    const hueAngle = Math.atan2(B, A);
    while (
      (!rgbColor ||
        rgbColor.r < 0 ||
        rgbColor.r > 1 ||
        rgbColor.g < 0 ||
        rgbColor.g > 1 ||
        rgbColor.b < 0 ||
        rgbColor.b > 1) &&
      chroma > 0.0001
    ) {
      chroma *= 0.98; // Reduce chroma by 2% each iteration
      A = chroma * Math.cos(hueAngle);
      B = chroma * Math.sin(hueAngle);
      labColor = { mode: 'oklab', l: L, a: A, b: B };
      rgbColor = rgb(labColor);
    }

    if (
      rgbColor &&
      rgbColor.r >= 0 &&
      rgbColor.r <= 1 &&
      rgbColor.g >= 0 &&
      rgbColor.g <= 1 &&
      rgbColor.b >= 0 &&
      rgbColor.b <= 1
    ) {
      colorScale.push(formatHex(rgbColor));
    } else {
      // Fallback to neutral gray if conversion fails
      const grayValue = Math.round(L * 255);
      const grayHex = `#${grayValue.toString(16).padStart(2, '0').repeat(3)}`;
      colorScale.push(grayHex);
    }
  }

  return colorScale;
}

export function generateColorPalette(cssColorSpecifier: string): ColorPalette {
  const scale = generateColorScale(cssColorSpecifier);

  return {
    100: scale[0],
    200: scale[1],
    300: scale[2],
    400: scale[3],
    500: scale[4],
    600: scale[5],
    700: scale[6],
    800: scale[7],
    900: scale[8],
    1000: scale[9],
    1100: scale[10],
  };
}

export const SMTailwindMinPixelBreakpointWidth = 640 as const;
export const MDTailwindMinPixelBreakpointWidth = 768 as const;
export const LGTailwindMinPixelBreakpointWidth = 1024 as const;
export const XLTailwindMinPixelBreakpointWidth = 1280 as const;
export const XXLTailwindMinPixelBreakpointWidth = 1536 as const;
